
      import Cropper from "@/vendor/cropperjs/cropper.js"

      const CROP_SIZE = 1024

      export default {
        mounted() {
          this.cropper = null
          this.pendingFile = null

          // Delegated: LiveView re-renders the upload input and button.
          this.el.addEventListener("click", (event) => {
            if (event.target.closest && event.target.closest("#avatar-change")) {
              this.pick()
            }
          })
          this.el.addEventListener("change", (event) => {
            if (event.target && event.target.id === "avatar-pick") {
              this.onPick(event)
            }
          })
        },
        unmounted() {
          this.teardown()
        },
        picker() {
          return this.el.querySelector("#avatar-pick")
        },
        fileInput() {
          return this.el.querySelector("[data-role=avatar-upload]")
        },
        pick() {
          const button = this.el.querySelector("#avatar-change")
          const input = this.picker()
          if (!button || button.disabled || !input) return
          input.click()
        },
        onPick(event) {
          const file = event.target.files && event.target.files[0]
          event.target.value = ""
          if (!file) return
          if (!/^image\/(jpeg|png)$/.test(file.type)) {
            this.pushEvent("avatar_failed", {})
            return
          }
          const reader = new FileReader()
          reader.onload = () => this.openModal(file, reader.result)
          reader.onerror = () => this.pushEvent("avatar_failed", {})
          reader.readAsDataURL(file)
        },
        openModal(file, dataUrl) {
          this.teardown()
          this.pendingFile = file

          const overlay = document.createElement("div")
          overlay.id = "avatar-crop-overlay"
          overlay.className =
            "fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4"
          overlay.innerHTML = `
            <div class="w-full max-w-sm rounded-xl border border-base-300 bg-base-100 p-4 shadow-2xl">
              <div class="flex items-center justify-between">
                <p class="text-base font-semibold tracking-tight">Crop your picture</p>
                <button type="button" id="avatar-crop-close" aria-label="Close"
                  class="text-base-content/45 transition hover:text-base-content">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                    class="h-5 w-5"><path d="M6 6l12 12M18 6L6 18" stroke-linecap="round"/></svg>
                </button>
              </div>
              <p class="mt-1 text-xs text-base-content/55">
                Drag to frame. The buttons rotate and zoom.
              </p>
              <div class="mt-3 aspect-square overflow-hidden rounded-lg bg-base-200">
                <img id="avatar-crop-image" src="" alt="" class="hidden" />
              </div>
              <div class="mt-3 flex items-center justify-center gap-2">
                <button type="button" id="avatar-crop-rotate-left"
                  class="flex h-10 w-10 items-center justify-center rounded-lg border border-base-300 text-lg transition hover:bg-base-200">&#8634;</button>
                <button type="button" id="avatar-crop-zoom-out"
                  class="flex h-10 w-10 items-center justify-center rounded-lg border border-base-300 text-lg transition hover:bg-base-200">&minus;</button>
                <button type="button" id="avatar-crop-zoom-in"
                  class="flex h-10 w-10 items-center justify-center rounded-lg border border-base-300 text-lg transition hover:bg-base-200">+</button>
                <button type="button" id="avatar-crop-rotate-right"
                  class="flex h-10 w-10 items-center justify-center rounded-lg border border-base-300 text-lg transition hover:bg-base-200">&#8635;</button>
              </div>
              <div class="mt-4 flex gap-2">
                <button type="button" id="avatar-crop-cancel"
                  class="i4g-btn-ghost flex-1"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="h-4 w-4"><path d="M6 6l12 12M18 6L6 18" stroke-linecap="round"/></svg> Cancel</button>
                <button type="button" id="avatar-crop-confirm"
                  class="i4g-btn-primary flex-1"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="h-4 w-4"><path d="M5 13l4 4L19 7" stroke-linecap="round" stroke-linejoin="round"/></svg> Use this picture</button>
              </div>
            </div>`
          document.body.appendChild(overlay)

          const image = overlay.querySelector("#avatar-crop-image")
          image.src = dataUrl
          image.classList.remove("hidden")
          this.cropper = new Cropper(image, {
            viewMode: 1,
            aspectRatio: 1,
            dragMode: "move",
            autoCropArea: 1,
            checkOrientation: false,
            checkCrossOrigin: false,
            responsive: false,
            scalable: false,
            cropBoxMovable: false,
            cropBoxResizable: false,
          })

          const on = (selector, handler) => {
            const node = overlay.querySelector(selector)
            if (node) node.addEventListener("click", handler)
          }
          on("#avatar-crop-rotate-left", () => this.cropper && this.cropper.rotate(-90))
          on("#avatar-crop-rotate-right", () => this.cropper && this.cropper.rotate(90))
          on("#avatar-crop-zoom-in", () => this.cropper && this.cropper.zoom(0.1))
          on("#avatar-crop-zoom-out", () => this.cropper && this.cropper.zoom(-0.1))
          on("#avatar-crop-cancel", () => this.closeModal())
          on("#avatar-crop-confirm", () => this.confirm())
          on("#avatar-crop-close", () => this.closeModal())
          overlay.addEventListener("click", (event) => {
            if (event.target === overlay) this.closeModal()
          })

          this.onKeydown = (event) => {
            if (event.key === "Escape") this.closeModal()
          }
          document.addEventListener("keydown", this.onKeydown)
        },
        confirm() {
          if (!this.cropper) return
          const canvas = this.cropper.getCroppedCanvas({
            width: CROP_SIZE,
            height: CROP_SIZE,
            fillColor: "#fff",
            imageSmoothingQuality: "high",
          })

          if (!canvas) {
            this.closeModal()
            this.pushEvent("avatar_failed", {})
            return
          }

          canvas.toBlob((blob) => {
            if (!blob) {
              this.closeModal()
              this.pushEvent("avatar_failed", {})
              return
            }

            const base = ((this.pendingFile && this.pendingFile.name) || "avatar").replace(/\.[^.]+$/, "")
            const cropped = new File([blob], `${base}.jpg`, {
              type: "image/jpeg",
              lastModified: Date.now(),
            })

            const input = this.fileInput()
            if (!input) {
              this.closeModal()
              this.pushEvent("avatar_failed", {})
              return
            }

            const dataTransfer = new DataTransfer()
            dataTransfer.items.add(cropped)
            input.files = dataTransfer.files
            input.dispatchEvent(new Event("change", { bubbles: true }))
            this.closeModal()
          }, "image/jpeg", 0.92)
        },
        closeModal() {
          this.teardown()
        },
        teardown() {
          if (this.cropper) {
            this.cropper.destroy()
            this.cropper = null
          }
          const overlay = document.getElementById("avatar-crop-overlay")
          if (overlay) overlay.remove()
          if (this.onKeydown) {
            document.removeEventListener("keydown", this.onKeydown)
            this.onKeydown = null
          }
          this.pendingFile = null
        },
      }
    